import { useEffect, useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore, type KeyboardEvent, type ReactElement, type Ref } from "react";
import { BODY_TYPE, FRAMES, SPACING } from "odyssey-shared";
import { GlyphButton, HudFrame, TAB_METRICS, centreOffset, logical } from "../../ui/kit/index.ts";
import { localStore, type SettingsStore } from "../settings.ts";
import { ChatBar, PeekLines, usePeek } from "./bar.tsx";
import { draftScopeIcon } from "./draft-scope.ts";
import { EntryRow } from "./entry-row.tsx";
import { ChatGrip, GRIP_SIZE, SizeLabel } from "./grip.tsx";
import { LogLine } from "./log.tsx";
import type { NameMatch } from "./lookup.ts";
import { CHAT_BAR, barRect, chatMode, focusAfterToggle, minimisedOn, readMinimised, writeMinimised, type ChatFocus } from "./minimise.ts";
import { sharedFirstNames } from "./names.ts";
import { Note, noteShows, useSecondsLeft } from "./note.tsx";
import { ChatPhoneButton } from "./phone-button.tsx";
import { phoneOpenAfter, phoneUnread } from "./phone.ts";
import { ENTRY_ROW, pickScope, pickerScopes, speakingTab } from "./picker.ts";
import { useScopeTip } from "./scope-tip.tsx";
import { chatRect, fitChatSize, readChatSize } from "./size.ts";
import { ChatSizeSetting } from "./sizing.ts";
import { STEPPER_START, chooseEntry, dismissList, editDraft, shownEntries, stepChat, stepperKey, syncStepper } from "./stepper.ts";
import { STEPS_ID, StepsList, entryId } from "./steps.tsx";
import type { ChatSource } from "./store.ts";
import { CHAT_TAB_PANEL_ID, ChatTabRow, chatTabId, useChatTabs } from "./tab-row.tsx";
import { labelDraft, replyTarget, tabDraft, type ScopeTab } from "./tabs.ts";
import { say } from "../../words.ts";

/**
 * The chat panel (ADR-068 decision 8 and its amendments; the brief's chat panel, a visual reference only): the log,
 * a note line and the entry row in a HUD frame filling the HUD's chat slot at bottom left. The log keeps its lines in
 * Noto Sans in each scope's colour, with words that tell the kinds apart as well (`log.tsx`), and another player's
 * name in it starts a whisper to them. The note line counts down the seconds while the account's messages wait,
 * says a reserved command is not open, or hints at the command being typed: its options, the current one marked,
 * with that option's description (`note.tsx`). The input owns the keyboard while it has focus (the input router,
 * CLIENT.md 7.3), and steps through commands (`stepper.ts`): the list over the log (`steps.tsx`) offers commands,
 * names and the reply's picker, Up and Down move through it, and Tab, Enter or a click chooses; Enter otherwise sends
 * and leaves the input, or on an empty input only leaves it; Escape, answered by the router's layers the page opens,
 * closes the list first and then leaves the input, keeping what was typed. A tab row over the log (`tab-row.tsx`,
 * ADR-068's amendment "scope tabs, and Help's icon") filters it to a scope and counts what each scope's tab has not
 * shown. A draft that is no command speaks to the chosen tab's scope (`tabs.ts`) until the entry row's scope picker
 * picks another (`entry-row.tsx`, `picker.ts`; the amendment "the entry row as redrawn, and the scope picker"), and
 * choosing a tab gives the draft back to the tab.
 *
 * The panel minimises (`minimise.ts`, `bar.tsx`; the amendment "the panel minimises"): the control at the tab row's
 * end folds it to a bar on its bottom edge, kept per browser, and the world shows through the rest of the slot. The
 * one input stays mounted in every state, so Enter types from the bar without opening the log, Escape returns to
 * the bar, and the field keeps its focus when the panel folds while the player types. A whisper received peeks above
 * the bar for a time and every scope counts what is unread on the bar. A tap or a click on the bar, or its open
 * button, opens the panel; whichever of the two controls had the keyboard's focus hands it to the other.
 *
 * The panel resizes (`size.ts`, `sizing.ts`, `grip.tsx`; ADR-061's amendment "the chat panel resizes"): the grip at
 * the tab row's end, the panel's top-right corner, and Settings' Chat size change the size the page keeps per
 * browser; the panel draws what of it fits the area beside the HUD's fixed parts, anchored at the slot's bottom-left
 * corner, and shows its size and a ring while the grip is dragged or focused. Under 632 logical pixels wide, where the
 * ability bar reaches the slot, the slot stands above the XP bar and the panel is minimised until the viewer opens or
 * folds it, a choice kept from then on (`minimisedOn`; ADR-061's amendment of 2026-10-06). A window dragged across
 * that width with no choice kept folds or opens the panel in place: the draft and the field's focus stay, the grip's
 * ring and size label go with the grip, and the control that leaves hands the keyboard's focus to the one that takes
 * its place, as a press of either does.
 *
 * The component imports no stylesheet, so it renders to static markup under `bun test`; `mount.tsx` imports
 * `chat.css`.
 */

/**
 * The note line's height: the caption role's line.
 */
const NOTE_LINE = BODY_TYPE.caption.lineHeight;

/**
 * The gap between the tab row, the log, the note line and the entry row, the design's.
 */
const GAP = SPACING[0];

/**
 * The padding inside the panel's HUD frame, the design's.
 */
export const CHAT_FRAME_PADDING = 1;

/**
 * The note line's id, which the input names as its description.
 */
const NOTE_ID = "ot-chat-note";

/**
 * The id of the line naming whom a draft under Whisper replies to, which the input names as a description too.
 */
const REPLY_ID = "ot-chat-reply";

/**
 * How far above the body's foot the stepper's list sits: over the note line, the entry row and their gaps.
 */
const LIST_BOTTOM = NOTE_LINE + ENTRY_ROW.height + 2 * GAP;

/**
 * How far above the body's foot the stepper's list sits over the minimised bar: a pixel clear of the bar's top edge.
 */
const BAR_LIST_BOTTOM = CHAT_BAR.height - FRAMES.hud.insets.bottom + 1;

/**
 * How far above the body's foot the stepper's list sits over the bar while the note shows above the bar: a pixel
 * clear of the note's line.
 */
const BAR_NOTE_LIST_BOTTOM = CHAT_BAR.height + CHAT_BAR.peekFoot - FRAMES.hud.insets.bottom + NOTE_LINE + 1;

/**
 * The minimise control's side, the design's.
 */
const MINIMISE_SIZE = 12;

/**
 * What the input and the panel's control share: the input element, what to do as it takes or loses focus, and
 * the router layers the stepper's list and the scope picker's list hold while they show.
 */
export interface ChatInputHooks {
  /** Where the page keeps the input element, to focus it on Enter. */
  readonly inputRef: Ref<HTMLInputElement>;
  /** Told when the input takes focus. */
  readonly onOpen: () => void;
  /** Told when the input loses focus. */
  readonly onLeave: () => void;
  /** Leaves the input, closing the page's layer. */
  readonly leave: () => void;
  /** Gives the input the focus, as a click on a name in the log does. */
  readonly focus: () => void;
  /**
   * Holds a layer on the router while the list shows, whose closing by Escape dismisses the list, or lets it go.
   *
   * @param shown Whether the list shows.
   * @param onDismiss Told when Escape closes the list's layer.
   */
  readonly list: (shown: boolean, onDismiss: () => void) => void;
  /**
   * Holds a layer on the router while the scope picker's list shows, whose closing by Escape closes the list, or
   * lets it go.
   *
   * @param shown Whether the picker's list shows.
   * @param onDismiss Told when Escape closes the layer.
   */
  readonly picker: (shown: boolean, onDismiss: () => void) => void;
}

/**
 * The chat panel. Its log keeps the newest line in view unless the player has scrolled back, and keeps it in view
 * again once the log is emptied, so the lines after a `/clear` are followed.
 *
 * @param props The `store` it reads and sends through, the `hooks` the page's control shares with the input,
 *   the page's clock (`now`) the waits and the peeks are counted on, where the chosen tab and the folded choice
 *   are kept (`tabStore`), and the page's chat size setting (`sizing`), one of the panel's own over `tabStore` when
 *   the page gives none.
 * @returns The panel in the chat slot, or nothing before any gateway has spoken chat on the page.
 */
export function ChatPanel({ store, hooks, now = Date.now, tabStore = localStore(), sizing }: { readonly store: ChatSource; readonly hooks: ChatInputHooks; readonly now?: () => number; readonly tabStore?: SettingsStore | null; readonly sizing?: ChatSizeSetting | undefined }): ReactElement | null {
  const { shown, connected, lines, channels, party, throttledUntil, lookupWaitUntil, layout, whisperAsked } = useSyncExternalStore(store.subscribe, store.getSnapshot, store.getSnapshot);
  const [draft, setDraft] = useState("");
  const [focused, setFocused] = useState(false);
  const [stepper, setStepper] = useState(STEPPER_START);
  const [picked, setPicked] = useState<ScopeTab | null>(null);
  const [keptMinimised, setKeptMinimised] = useState(() => readMinimised(tabStore));
  const touch = layout.touch === true;
  const [phoneOpen, setPhoneOpen] = useState(false);
  const phoneFocus = useRef(false);
  const phoneClosed = touch && !phoneOpen;
  const minimised = touch ? !phoneOpen : minimisedOn(keptMinimised, layout);
  const [own] = useState(() => sizing ?? new ChatSizeSetting(readChatSize(tabStore), tabStore));
  const sized = sizing ?? own;
  const wished = useSyncExternalStore(sized.subscribe, sized.getSnapshot, sized.getSnapshot);
  const [resizing, setResizing] = useState(false);
  const [pickerDismissed, setPickerDismissed] = useState(0);
  const gripButton = useRef<HTMLButtonElement>(null);
  const minimiseButton = useRef<HTMLButtonElement>(null);
  const openButton = useRef<HTMLButtonElement>(null);
  const focusNext = useRef<"minimise" | "open" | null>(null);
  const foldedBefore = useRef(minimised);

  /**
   * Which of the panel's controls has the keyboard's focus now.
   *
   * @returns The minimise control, the grip, the open control, or anything else.
   */
  const focusedControl = (): ChatFocus => {
    const active = typeof document === "undefined" ? null : document.activeElement;
    return active === null ? "other" : active === minimiseButton.current ? "minimise" : active === gripButton.current ? "grip" : active === openButton.current ? "open" : "other";
  };
  if (foldedBefore.current !== minimised) {
    foldedBefore.current = minimised;
    focusNext.current ??= focusAfterToggle(focusedControl(), minimised);
  }
  const log = useRef<HTMLDivElement>(null);
  const scopeTip = useScopeTip(store.cardOf);
  const pinned = useRef(true);
  const current = useRef(draft);
  current.current = draft;
  const seconds = useSecondsLeft(throttledUntil, now);
  const lookupSeconds = useSecondsLeft(lookupWaitUntil, now);
  const context = store.stepContext();
  const tabs = useChatTabs(lines, channels, context.self, tabStore, minimised, party);
  const peeking = usePeek(lines, minimised, now, context.self);
  const shared = useMemo(() => sharedFirstNames(lines), [lines]);
  const view = stepChat(draft, context);
  const synced = syncStepper(stepper, draft, view);
  const entries = focused ? shownEntries(synced, draft, view) : [];
  const listShown = entries.length > 0;
  const highlight = Math.min(synced.highlight, Math.max(0, entries.length - 1));
  const waiting = focused && view.lookup !== null ? lookupSeconds : 0;
  useLayoutEffect(() => {
    const element = log.current;
    if (lines.length === 0) {
      pinned.current = true;
    }
    if (element !== null && pinned.current) {
      element.scrollTop = element.scrollHeight;
    }
  }, [lines, tabs.shown, minimised]);
  useLayoutEffect(() => {
    const target = focusNext.current;
    focusNext.current = null;
    (target === "open" ? openButton.current : target === "minimise" ? minimiseButton.current : null)?.focus();
    if (minimised) {
      setResizing(false);
    }
  }, [minimised]);
  const askedSeq = useRef(whisperAsked?.seq ?? 0);
  useEffect(() => {
    if (whisperAsked !== undefined && whisperAsked.seq !== askedSeq.current) {
      askedSeq.current = whisperAsked.seq;
      write(`/w ${whisperAsked.firstName} ${whisperAsked.familyName} `, STEPPER_START);
      hooks.focus();
    }
  }, [whisperAsked?.seq]);
  useEffect(() => {
    if (phoneOpen && phoneFocus.current) {
      phoneFocus.current = false;
      hooks.focus();
    }
  }, [phoneOpen]);
  useEffect(() => {
    store.want(focused ? view.lookup : null, view.command?.id === "invite" ? "invite" : "name");
  }, [focused, view.lookup, view.command?.id]);
  useEffect(() => {
    hooks.list(listShown, () => setStepper((state) => dismissList(state, current.current)));
  }, [listShown]);
  if (!shown) {
    return null;
  }
  const size = fitChatSize(wished, layout);
  const slot = chatRect(size, layout);
  const rect = minimised ? barRect(slot) : slot;
  const mode = chatMode({ minimised, typing: focused, peeking: peeking.length > 0 });
  const noted = noteShows(seconds, view, focused);
  /**
   * Folds the panel to its bar or opens it, keeps the choice, and hands the keyboard's focus from the control that
   * leaves to the one that takes its place. The grip leaves with the tab row without losing focus first, so the
   * ring and the size it showed are cleared here.
   *
   * @param fold Whether to minimise.
   */
  const toggle = (fold: boolean): void => {
    focusNext.current = focusAfterToggle(focusedControl(), fold);
    setResizing(false);
    pinned.current = true;
    setKeptMinimised(fold);
    writeMinimised(fold, tabStore);
  };
  /**
   * Opens the panel from the phone's Chat button, which gives the input the focus once the panel is drawn.
   */
  const openPhone = (): void => {
    phoneFocus.current = true;
    pinned.current = true;
    setPhoneOpen(phoneOpenAfter("button"));
  };
  /**
   * Closes the panel on a touch page, leaving the input and keeping its draft.
   *
   * @param event The close control or a tap outside the panel.
   */
  const closePhone = (event: "close" | "outside"): void => {
    hooks.leave();
    setPhoneOpen(phoneOpenAfter(event));
  };
  const speaking = speakingTab(picked, tabs.shown, tabs.open);
  /**
   * Writes a draft the stepper chose and moves the stepper with it.
   *
   * @param next The draft.
   * @param state The stepper's state after the choice.
   */
  const write = (next: string, state: typeof stepper): void => {
    setDraft(next);
    setStepper(state);
  };
  /**
   * Sends the draft, clearing it once it is dealt with; an emptied input is left.
   */
  const submit = (): void => {
    setPickerDismissed((count) => count + 1);
    const { clear } = store.submit(tabDraft(draft, speaking));
    if (clear) {
      write("", STEPPER_START);
      hooks.leave();
    }
  };
  /**
   * Chooses an entry of the list, as a click does.
   *
   * @param index The entry's place.
   */
  const choose = (index: number): void => {
    const entry = entries[index];
    if (entry !== undefined) {
      const chosen = chooseEntry(synced, entry);
      write(chosen.draft, chosen.state);
    }
  };
  /**
   * Starts a whisper to a player named in the log: the input holds `/w First Family ` and takes the focus.
   *
   * @param name The player.
   */
  const whisperTo = (name: NameMatch): void => {
    write(`/w ${name.firstName} ${name.familyName} `, STEPPER_START);
    hooks.focus();
  };
  /**
   * Picks the scope plain text goes to: a draft whose command named another scope keeps its message alone, and
   * the input takes the focus, since a scope is picked to type in.
   *
   * @param scope The scope.
   */
  const pick = (scope: ScopeTab): void => {
    const next = pickScope(draft, scope);
    setPicked(scope);
    if (next !== draft) {
      setDraft(next);
      setStepper((state) => editDraft(state, next));
    }
    hooks.focus();
  };
  /**
   * The input's keys: Up, Down and Tab move through the list while it shows; Enter chooses where the page would
   * refuse the draft and a list shows, and sends otherwise. Neither Enter nor a key the list takes reaches the
   * router or the game. A key an input method's composition takes is left to it: one marked composing, and one with
   * the key code 229, which is how WebKit gives the Enter that ends a composition, after the composition has ended.
   *
   * @param event The key.
   */
  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>): void => {
    const key = event.key;
    if ((key !== "Enter" && key !== "Tab" && key !== "ArrowDown" && key !== "ArrowUp") || event.nativeEvent.isComposing || event.nativeEvent.keyCode === 229) {
      return;
    }
    const outcome = stepperKey(synced, key, draft, focused ? view : { ...view, list: null });
    if (outcome.kind === "pass") {
      return;
    }
    event.preventDefault();
    if (outcome.kind === "send") {
      submit();
    } else if (outcome.kind === "move") {
      setStepper(outcome.state);
    } else {
      write(outcome.draft, outcome.state);
    }
  };
  const reply = replyTarget(speaking, draft, context.whisperers);
  const note = (
    <p className={minimised ? "ot-chat__note ot-chat__note--over" : "ot-chat__note"} id={NOTE_ID} data-chat="note" aria-live="polite" style={{ height: logical(minimised && !noted ? 0 : NOTE_LINE), marginTop: minimised && noted ? logical(CHAT_BAR.peekGap) : undefined }}>
      <Note seconds={seconds} view={view} focused={focused} />
    </p>
  );
  const unread = phoneUnread(tabs.counts);
  return (
    <>
      {phoneClosed ? <ChatPhoneButton area={layout} unread={unread} onOpen={openPhone} /> : null}
      {touch && phoneOpen ? <div className="ot-chat__scrim" data-chat="scrim" onPointerDown={() => closePhone("outside")} /> : null}
      <div className="ot-chat" data-hud-slot="chat" data-chat="panel" data-chat-mode={mode} data-chat-phone={touch ? (phoneOpen ? "open" : "closed") : undefined} aria-hidden={phoneClosed ? true : undefined} data-chat-resizing={resizing && !minimised ? "true" : undefined} style={{ left: logical(rect.left), top: logical(rect.top), width: logical(rect.width), height: logical(rect.height) }}>
        {minimised && !touch ? (
          <div className="ot-chat__above" data-chat="above" style={{ left: logical(CHAT_BAR.gap), right: logical(CHAT_BAR.gap), bottom: logical(CHAT_BAR.height + CHAT_BAR.peekFoot) }}>
            <PeekLines lines={peeking} self={context.self} shared={shared} />
            {note}
          </div>
        ) : null}
        {resizing && !minimised ? <SizeLabel size={size} /> : null}
        <HudFrame padding={mode === "mintype" ? CHAT_BAR.typingPadding : CHAT_FRAME_PADDING}>
          <div ref={scopeTip.body} className="ot-chat__body" style={{ gap: logical(minimised ? 0 : GAP) }} {...scopeTip.handlers}>
            {minimised ? null : (
              <div className="ot-chat__head" data-chat="head" style={{ gap: logical(GAP) }}>
                <ChatTabRow
                  open={tabs.open}
                  shown={tabs.shown}
                  counts={tabs.counts}
                  onChoose={(tab) => {
                    pinned.current = true;
                    setPicked(null);
                    tabs.choose(tab);
                  }}
                />
                <span className="ot-chat__head-control" style={{ marginTop: logical(centreOffset(TAB_METRICS.chosenHeight, MINIMISE_SIZE)) }}>
                  {touch ? <GlyphButton glyph="close" label={say("chat.closeChat")} size={MINIMISE_SIZE} keepFocus buttonRef={minimiseButton} onClick={() => closePhone("close")} data={{ "data-chat": "close" }} /> : <GlyphButton glyph="minus" label={say("chat.minimise")} size={MINIMISE_SIZE} keepFocus buttonRef={minimiseButton} onClick={() => toggle(true)} data={{ "data-chat": "minimise" }} />}
                </span>
                {touch ? null : (
                  <span className="ot-chat__head-control" style={{ marginTop: logical(centreOffset(TAB_METRICS.chosenHeight, GRIP_SIZE)) }}>
                    <ChatGrip wish={wished} area={layout} onResize={(next, keep) => sized.setSize(next, keep)} onActive={setResizing} gripRef={gripButton} />
                  </span>
                )}
              </div>
            )}
            {minimised ? null : (
              <div className="ot-chat__tabpanel" role="tabpanel" id={CHAT_TAB_PANEL_ID} aria-labelledby={chatTabId(tabs.shown)} data-chat="tabpanel" data-chat-shown={tabs.shown}>
                <div
                  ref={log}
                  className="ot-chat__log ot-scroll"
                  role="log"
                  aria-label={say("chat.panel")}
                  data-chat="log"
                  onScroll={(event) => {
                    const element = event.currentTarget;
                    pinned.current = element.scrollTop + element.clientHeight >= element.scrollHeight - 1;
                  }}
                >
                  {tabs.lines.map((line) => (
                    <LogLine key={line.id} line={line} self={context.self} shared={shared} onName={whisperTo} onMenu={store.menuOn} />
                  ))}
                </div>
              </div>
            )}
            {focused ? <StepsList kind={view.list?.kind ?? null} entries={entries} highlight={highlight} onChoose={choose} waitSeconds={waiting} bottom={minimised ? (noted ? BAR_NOTE_LIST_BOTTOM : BAR_LIST_BOTTOM) : LIST_BOTTOM} /> : null}
            {minimised ? null : note}
            {minimised && !touch ? <ChatBar counts={tabs.counts} onOpen={() => toggle(false)} openRef={openButton} away={focused} /> : null}
            <EntryRow
              input={{
                inputRef: hooks.inputRef,
                draft,
                focused,
                onDraft: (next) => {
                  setDraft(next);
                  setStepper((state) => editDraft(state, next));
                },
                onKeyDown,
                onFocus: () => {
                  setFocused(true);
                  if (touch) {
                    setPhoneOpen(phoneOpenAfter("focus"));
                  }
                  setStepper((state) => (state.dismissed === null ? state : { ...state, dismissed: null }));
                  hooks.onOpen();
                },
                onBlur: () => {
                  setFocused(false);
                  setPickerDismissed((count) => count + 1);
                  hooks.onLeave();
                },
                describedBy: reply === null ? NOTE_ID : `${NOTE_ID} ${REPLY_ID}`,
                listId: listShown ? STEPS_ID : null,
                activeId: listShown ? entryId(highlight) : null,
                reachedByPageOnly: phoneClosed,
              }}
              connected={connected}
              scope={draftScopeIcon(labelDraft(draft, speaking))}
              scopes={pickerScopes(tabs.open)}
              onPick={pick}
              reply={reply}
              replyId={REPLY_ID}
              onSubmit={submit}
              gap={GAP}
              layer={hooks.picker}
              shape={minimised ? (focused ? "compact" : "away") : "full"}
              pickerDismissed={pickerDismissed}
            />
            {scopeTip.tooltip}
          </div>
        </HudFrame>
      </div>
    </>
  );
}
